import { useEffect, useRef, useState } from 'react'
import { getPublicConfig, signIn } from '../lib/api'
import { renderSignInButton } from '../lib/google'

interface Props {
  /** Explanation carried over from an expired session, when there is one. */
  notice?: string
  onSignedIn: () => void | Promise<void>
}

/**
 * The whole setup flow, for the hosted app: sign in with Google. There is no
 * setup code to paste and no OAuth client to distribute — the administrator
 * configures the deployment once and adds people to the allowlist, so a new
 * user's first run is a single button.
 */
export default function SignInPage({ notice, onSignedIn }: Props): JSX.Element {
  const buttonRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const config = await getPublicConfig()
        if (cancelled || !buttonRef.current) return
        await renderSignInButton(buttonRef.current, {
          clientId: config.googleClientId,
          ...(config.hostedDomain ? { hostedDomain: config.hostedDomain } : {}),
          onCredential: (credential) => {
            setBusy(true)
            setError('')
            void signIn(credential)
              .then(() => onSignedIn())
              .catch((e: unknown) =>
                setError(e instanceof Error ? e.message : 'Sign-in failed. Please try again.')
              )
              .finally(() => setBusy(false))
          },
          onError: setError
        })
        if (!cancelled) setReady(true)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not start Google sign-in.')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [onSignedIn])

  return (
    <div className="signin-page">
      <div className="signin-card">
        <div className="signin-brand">
          Autom<span>8</span>
        </div>
        <h1>Sign in to continue</h1>
        <p className="subtitle">
          Use your work Google account. Your administrator gives you access — there is nothing to
          install and nothing to set up.
        </p>

        {notice && <div className="alert info">{notice}</div>}
        {error && <div className="alert error">{error}</div>}

        <div className="signin-button-row">
          <div ref={buttonRef} />
          {!ready && !error && <div className="center-note">Loading Google sign-in…</div>}
          {busy && <div className="center-note">Signing you in…</div>}
        </div>

        <p className="signin-foot">
          Signing in tells Autom8 who you are. It does not give this app access to your email,
          files, or anything else in your Google account.
        </p>
        {/* The guide needs no session, which is the point: its first page is
            how to get in. */}
        <p className="signin-foot">
          New to Autom8?{' '}
          <a href="/guide" target="_blank" rel="noopener">
            Read the guide
          </a>
          .
        </p>
      </div>
    </div>
  )
}
